import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import BzmIcon from 'components/icons/BzmIcon';
import BzmSearchInput from 'components/forms/BzmSearchInput/BzmSearchInput';
import BlazeInputDropdown, { BlazeDropdownItem } from 'components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import BzmDateRangePicker, {
  DateRange,
  FixedTimeRange,
} from 'views/Analytics/AnalyticsMockService/Reporting/Pickers/BzmDateRangePicker/BzmDateRangePicker';
import { periodToDisplayRange } from 'views/Admin/_shared/dateApi';
import { PeriodPreset, PeriodSelection, UsageWorkspace } from 'views/Admin/UsageReport/types';
import { AUDIT_EVENT_TYPES, AuditEventType, AuditLogFilters as Filters } from './types';

interface AuditLogFiltersProps {
  filters: Filters;
  workspaces: UsageWorkspace[];
  isRefreshing: boolean;
  onChange: (next: Filters) => void;
  onRefresh: () => void;
}

const SEARCH_DEBOUNCE_MS = 250;
const ALL = 'ALL';
// Sentinel workspace_id used for account-wide audit events (see AccountSettingsService.ACCOUNT_WIDE_WORKSPACE_ID).
const ACCOUNT_LEVEL_WORKSPACE_ID = 0;
const AUDIT_PRESETS: PeriodPreset[] = ['LAST_MONTH', 'LAST_QUARTER', 'LAST_6_MONTHS', 'LAST_YEAR'];

const periodToFixed = (period: PeriodSelection): FixedTimeRange | null =>
  period.kind === 'preset' ? (period.value as FixedTimeRange) : null;

const isAuditPreset = (value: FixedTimeRange): value is PeriodPreset =>
  (AUDIT_PRESETS as readonly string[]).includes(value);

const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  filters,
  workspaces,
  isRefreshing,
  onChange,
  onRefresh,
}) => {
  const { t } = useTranslation('admin');
  const [searchInput, setSearchInput] = useState(filters.keyword);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef(filters);
  const onChangeRef = useRef(onChange);
  filtersRef.current = filters;
  onChangeRef.current = onChange;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchInput !== filtersRef.current.keyword) {
        onChangeRef.current({ ...filtersRef.current, keyword: searchInput });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const presetButtons = [
    { optionValue: 'LAST_MONTH' as FixedTimeRange, label: t('auditLog.lastMonth') },
    { optionValue: 'LAST_QUARTER' as FixedTimeRange, label: t('auditLog.lastQuarter') },
    { optionValue: 'LAST_6_MONTHS' as FixedTimeRange, label: t('auditLog.last6Months') },
    { optionValue: 'LAST_YEAR' as FixedTimeRange, label: t('auditLog.lastYear') },
  ];

  const handleFixedRangeChanged = (preset: FixedTimeRange) => {
    if (!isAuditPreset(preset)) return;
    onChange({ ...filters, period: { kind: 'preset', value: preset } });
  };

  const handleCustomRangeChanged = (range: DateRange) => {
    onChange({ ...filters, period: { kind: 'custom', from: range.from, to: range.to } });
  };

  const eventTypeItems: BlazeDropdownItem[] = [
    { id: ALL, name: t('auditLog.allEventTypes') },
    ...AUDIT_EVENT_TYPES.map(et => ({ id: et, name: t(`auditLog.eventTypes.${et}`) })),
  ];
  const selectedEventType: BlazeDropdownItem =
    eventTypeItems.find(item => item.id === (filters.eventType ?? ALL)) ?? eventTypeItems[0];

  const handleEventTypeChange = (item: BlazeDropdownItem) => {
    onChange({ ...filters, eventType: item.id === ALL ? null : (item.id as AuditEventType) });
  };

  const workspaceItems: BlazeDropdownItem[] = [
    { id: ALL, name: t('auditLog.allWorkspaces') },
    { id: ACCOUNT_LEVEL_WORKSPACE_ID, name: t('auditLog.accountLevel') },
    ...workspaces.map(w => ({ id: w.id, name: w.name })),
  ];
  const selectedWorkspace: BlazeDropdownItem =
    workspaceItems.find(item => item.id === (filters.workspaceId ?? ALL)) ?? workspaceItems[0];

  const handleWorkspaceChange = (item: BlazeDropdownItem) => {
    onChange({ ...filters, workspaceId: item.id === ALL ? null : Number(item.id) });
  };

  const displayRange: DateRange = useMemo(() => periodToDisplayRange(filters.period), [filters.period]);

  return (
    <div className="audit-log-filters">
      <div className="audit-log-filters__field">
        <label className="audit-log-filters__field-label">{t('auditLog.timeFrameLabel')}</label>
        <BzmDateRangePicker
          presets={presetButtons}
          presetsInPopover={true}
          showTimePicker={false}
          isCompact={true}
          fixedSelection={periodToFixed(filters.period)}
          dateRange={displayRange}
          fixedSelectionChanged={handleFixedRangeChanged}
          dateRangeChanged={handleCustomRangeChanged}
        />
      </div>

      <div className="audit-log-filters__field">
        <label className="audit-log-filters__field-label">{t('auditLog.eventTypeLabel')}</label>
        <BlazeInputDropdown
          id="qa-audit-log-event-type"
          items={eventTypeItems}
          selected={selectedEventType}
          onClick={handleEventTypeChange}
          isSearchable={false}
          dropdownWidth={200}
        />
      </div>

      <div className="audit-log-filters__field">
        <label className="audit-log-filters__field-label">{t('auditLog.workspaceLabel')}</label>
        <BlazeInputDropdown
          id="qa-audit-log-workspace"
          items={workspaceItems}
          selected={selectedWorkspace}
          onClick={handleWorkspaceChange}
          isSearchable={true}
          dropdownWidth={220}
        />
      </div>

      <div className="audit-log-filters__field audit-log-filters__search">
        <div className="audit-log-filters__search-row">
          <div className="audit-log-filters__search-input">
            <BzmSearchInput
              value={searchInput}
              onChange={setSearchInput}
              placeholder={t('auditLog.searchPlaceholder')}
            />
          </div>
          <Button variant="secondary" disabled={isRefreshing} onClick={onRefresh}>
            <BzmIcon variant="icon-bzm-sync" />
            <span className="ml-2">{t('auditLog.refresh')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogFilters;
